import styled from "@emotion/styled";
import React, { useMemo } from "react";
import Layout from "@components/Layout";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Observer } from "mobx-react-lite";
import { useStores } from "@stores";
import { RangesListProvider, useRangesListVm } from "./RangesListVm";
import { Column, Row } from "@src/components/Flex";
import { ROUTES } from "@src/constants";
import Img from "@components/Img";
import Button from "@components/Button";
import { useTheme } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import Card from "@components/Card";
import SearchAndFilterTab from "./SearchAndFilterTab";
import RangesTable from "./RangesTable";
import { themes } from "@src/themes/ThemeProvider";
import Skeleton from "react-loading-skeleton";
import useWindowSize from "@src/hooks/useWindowSize";
import { ReactComponent as BookIcon } from "@src/assets/icons/book.svg";

interface IProps {}

const Root = styled.div<{
  apySort?: boolean;
  liquiditySort?: boolean;
  balanceSort?: boolean;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-sizing: border-box;
  padding: 0 16px;
  width: 100%;
  min-height: 100%;
  max-width: calc(1160px + 32px);
  margin-bottom: 24px;
  margin-top: 40px;
  text-align: left;
  @media (min-width: 880px) {
    margin-top: 56px;
  }

  .balance-group {
    width: 20px;
    height: 20px;
    transform: ${({ balanceSort }) => (balanceSort ? "scale(1)" : "scale(1, -1)")};
  }

  .apy-group {
    width: 20px;
    height: 20px;
    transform: ${({ apySort }) => (apySort ? "scale(1)" : "scale(1, -1)")};
  }

  .liquidity-group {
    width: 20px;
    height: 20px;
    transform: ${({ liquiditySort }) => (liquiditySort ? "scale(1)" : "scale(1, -1)")};
  }
`;
const AboutRanges = styled(Column)`
  width: 100%;

  align-items: center;
  justify-content: space-between;

  @media (min-width: 880px) {
    flex-direction: row;
  }
`;
const Subtitle = styled(Text)`
  @media (min-width: 880px) {
    max-width: 560px;
  }
`;
const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  @media (min-width: 880px) {
    flex-direction: row;
    width: auto;
    align-items: center;
    gap: 12px;
  }
`;

const DocsButton = styled(Button)`
  gap: 8px;

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    fill: ${({ theme }) => theme.colors.blue500};
    transition: fill 0.4s;
  }

  &:hover svg path {
    fill: ${({ theme }) => theme.colors.button};
  }
`;

const Stats = styled.div<{ loggedIn?: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  @media (min-width: 880px) {
    grid-template-columns: ${({ loggedIn }) => (loggedIn ? "1fr 1fr 1fr" : "1fr 1fr")};
  }
`;
const RangesListImpl: React.FC<IProps> = () => {
  const vm = useRangesListVm();
  const { poolsStore, accountStore } = useStores();
  const theme = useTheme();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = useMemo(() => !!(width && width < 880), [width]);
  const whiteText = { color: themes.lightTheme.colors.white };
  return (
    <Layout>
      <Observer>
        {() => (
          <Root
            apySort={poolsStore.sortApy}
            liquiditySort={poolsStore.sortLiquidity}
            balanceSort={poolsStore.sortBalance}
          >
            <AboutRanges>
              <Column>
                <Row style={{ flexWrap: "wrap" }}>
                  <Text weight={500} size="large" fitContent>
                    Explore and Manage
                  </Text>
                  <SizedBox width={4} />
                  <Text weight={500} size="large" fitContent style={{ color: "#7075E9" }}>
                    Puzzle Ranges
                  </Text>
                </Row>
                <SizedBox height={4} />
                <Subtitle size="medium" fitContent>
                  View your active liquidity ranges or discover opportunities created by others.
                  <br />
                  Analyze token usage by price range, and decide where to provide concentrated liquidity in the Puzzle
                  ecosystem.
                </Subtitle>
              </Column>
              <SizedBox height={24} />
              <Actions>
                <DocsButton kind="secondary" fixed={isMobile} onClick={() => navigate(ROUTES.DOCS)}>
                  <BookIcon />
                  Docs
                </DocsButton>
                <Button fixed={isMobile} onClick={() => navigate(`${ROUTES.RANGES_CREATE}`)}>
                  <Img src={theme.images.icons.add} alt="add" />
                  <SizedBox width={12} />
                  Create a range
                </Button>
              </Actions>
            </AboutRanges>
            <SizedBox height={32} />
            <Stats loggedIn={accountStore.address != null}>
              {accountStore.address != null && (
                <Card
                  paddingDesktop="16px 20px"
                  type="image"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {vm.userInvestedAmount ? (
                    <>
                      <Column>
                        <Text type="secondary">My Investment Balance</Text>
                        <SizedBox height={4} />
                        <Text size="big" style={whiteText}>
                          ${vm.userInvestedAmount.toFormat(2)}
                        </Text>
                      </Column>
                      {vm.userInvestedAmount.gt(0) && (
                        <Button onClick={() => !vm.loading && vm.setShowOnlyUserRanges(!vm.showOnlyUserRanges)} size="medium" disabled={vm.loading}>
                          {vm.showOnlyUserRanges ? "All Ranges" : "My Ranges"}
                        </Button>
                      )}
                    </>
                  ) : (
                    <Column>
                      <Text type="secondary">My Investment Balance</Text>
                      <SizedBox height={4} />
                      <Skeleton width={85} height={24} baseColor="#8082C5" highlightColor="#F1F2FE" />
                    </Column>
                  )}
                </Card>
              )}
              <Card paddingDesktop="16px 20px">
                <Text type="secondary">Number Of Ranges</Text>
                <SizedBox height={4} />
                {vm.rangesInfo ? <Text size="big">{vm.rangesInfo?.totalPools}</Text> : <Skeleton width={34} />}
              </Card>
              <Card paddingDesktop="16px 20px">
                <Text type="secondary">Total Fact / Virtual Liquidity</Text>
                <SizedBox height={4} />
                {vm.rangesInfo ? (
                  <Text size="big">
                    ${vm.rangesInfo?.totalLiquidity.toFormat(2)} /{" "}
                    <Text type="secondary" size="big" style={{ display: "inline" }}>
                      ${vm.rangesInfo?.totalVirtualLiquidity.toFormat(2)}
                    </Text>
                  </Text>
                ) : (
                  <Skeleton width={240} />
                )}
              </Card>
            </Stats>
            <SizedBox height={32} />
            <SearchAndFilterTab />
            <SizedBox height={32} />
            <RangesTable />
          </Root>
        )}
      </Observer>
    </Layout>
  );
};

const RangesList: React.FC<IProps> = () => (
  <RangesListProvider>
    <RangesListImpl />
  </RangesListProvider>
);
export default RangesList;
